import axios from 'axios';
import { API_URL } from './types';

let logoutHandler: (() => void) | null = null;

export function setLogoutHandler(handler: () => void) {
  logoutHandler = handler;
}

export function getAccessToken(): string | null {
  return localStorage.getItem('accessToken');
}

export function getRefreshTokenValue(): string | null {
  return localStorage.getItem('refreshToken');
}

export function setTokens(accessToken: string, refreshToken: string) {
  if (!accessToken || !refreshToken) {
    throw Error('missing token');
  }
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
}

export function clearTokens() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
}

export async function refreshSession(): Promise<{ accessToken: string; refreshToken: string }> {
  const refreshToken = getRefreshTokenValue();
  if (!refreshToken) {
    throw new Error('No refresh token');
  }
  const res = await axios.post<{ accessToken: string; refreshToken: string }>(
    `${API_URL}/auth/refresh`,
    { refreshToken }
  );
  setTokens(res.data.accessToken, res.data.refreshToken);
  return res.data;
}

const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise: Promise<string> | null = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = getRefreshTokenValue();

      if (!refreshToken) {
        logoutHandler?.();
        return Promise.reject(error);
      }

      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post<{ accessToken: string; refreshToken: string }>(
              `${API_URL}/auth/refresh`,
              { refreshToken }
            )
            .then((res) => {
              setTokens(res.data.accessToken, res.data.refreshToken);
              refreshPromise = null;
              return res.data.accessToken;
            })
            .catch((err) => {
              refreshPromise = null;
              throw err;
            });
        }

        const newAccessToken = await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch {
        clearTokens();
        logoutHandler?.();
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
